import type { RecognitionScheduleWorkspace } from "@chro/shared";
import { parseFinanceOrigin, type FinanceOrigin } from "./finance-navigation";

export type RecognitionTab = "trades" | "matching" | "recognition" | "history";
export type RecognitionSearch = FinanceOrigin & {
  scope?: "pair" | "unpaired";
  pair?: string;
  trade?: string;
  schedule?: string;
  tab?: RecognitionTab;
  invalidFocus?: true;
};
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function parseRecognitionSearch(search: Record<string, unknown>): RecognitionSearch {
  const trade = typeof search.trade === "string" && uuid.test(search.trade) ? search.trade.toLowerCase() : undefined;
  const schedule = typeof search.schedule === "string" && uuid.test(search.schedule) ? search.schedule.toLowerCase() : undefined;
  const origin = parseFinanceOrigin(search);
  const invalidOrigin = (["financeMonth", "financeView", "financeCompany", "financeTab"] as const).some((key) => search[key] !== undefined && origin[key] === undefined);
  const invalidFocus = (search.trade !== undefined && !trade) || (search.schedule !== undefined && !schedule) || invalidOrigin || search.invalidFocus === true;
  return {
    ...origin,
    scope: search.scope === "unpaired" ? "unpaired" : undefined,
    pair: search.scope !== "unpaired" && typeof search.pair === "string" && search.pair.trim().length <= 80 ? search.pair.trim() || undefined : undefined,
    trade, schedule,
    tab: ["trades", "matching", "recognition", "history"].includes(String(search.tab)) ? search.tab as RecognitionTab : undefined,
    ...(invalidFocus ? { invalidFocus: true as const } : {}),
  };
}

/** Never substitute another execution or schedule for an unavailable reference. */
export function resolveRecognitionFocus(workspace: RecognitionScheduleWorkspace, search: Pick<RecognitionSearch, "trade" | "schedule">) {
  const trade = search.trade ? workspace.trades.find((candidate) => candidate.id === search.trade) ?? null : null;
  const entry = search.schedule ? workspace.entries.find((candidate) => candidate.id === search.schedule && (!search.trade || candidate.sourceTradeIds.includes(search.trade))) ?? null : null;
  const item = entry ? workspace.items.find((candidate) => candidate.itemId === entry.itemId) ?? null : null;
  const selectedTradeIds = search.trade ? trade ? [trade.id] : [] : entry ? entry.sourceTradeIds.filter((id) => workspace.trades.some((candidate) => candidate.id === id)) : [];
  return { trade, entry, item, selectedTradeIds,
    tradeUnavailable: !!search.trade && !trade,
    scheduleUnavailable: !!search.schedule && !entry,
  };
}
